import { logger } from "../../../utilities/logger";
import { OutlookUtils } from "../../../utilities/OutlookUtils";
import { ModeEnum } from "../components/enums/modeEnum";
import { DirectoryTreeType } from "../config/config";
import { IFolderLocation, ISharepointDocumentLocation } from "../interfaces/ISharepointDocument";
import { ISiteInfo, ISiteInfoLocation } from "../interfaces/ISiteInfo";
import { apiGetTessAccounts, apiGetTessProjects, apiPostTessEmail } from "./ApiService";

export interface ITessUserIdentifier {
    tenantId: string;
    accountId: string;
}

export interface ITessTokenRequest extends ITessUserIdentifier {
    code: string;
}

export interface ITessAccountsResult {
    allowLoadAllAccounts: boolean;
    accounts: ITessAccount[];
}

export interface ITessAccount {
    phone: string;
    name: string;
    id: string;
    email: string;
    dossierMetadata: ITessDossier[];
    directMatch: boolean;
    contacts: any[];
    accountNumber: string;
}

export interface ITessProjectFolders {
    finished: ITessProject[];
    drafts: ITessProject[];
    active: ITessProject[];
    account: ITessAccount;
}

export interface ITessProject {
    year: number | null;
    status: string;
    serviceName: string;
    period: string | null;
    name: string;
    id: string;
    dossierMetadata: ITessDossier[] | null;
}

export interface ITessDossier {
    itemId: string;
    driveId: string;
    name: string;
}

export enum TessProjectStatusEnum {
    Active = "active",
    Draft = "drafts",
    Finished = "finished",
}

export const TessProjectStatuses = [
    { key: TessProjectStatusEnum.Active },
    { key: TessProjectStatusEnum.Draft },
    { key: TessProjectStatusEnum.Finished }
];

export interface ITessProjectFolderLocation {
    id: string;
    name: string;
    treeType?: DirectoryTreeType;
    level?: number;
    isSelected?: boolean;
    canExpand?: boolean;
    expanded?: boolean;
    isLoading?: boolean;
    randomId: string;
    loaded?: boolean;
    projects: ITessProjectLocation[];
}

export interface ITessProjectLocation extends ITessProject {
    accountId: string;
    tessProjectId?: string;
    treeType?: DirectoryTreeType;
    level?: number;
    isSelected?: boolean;
    canExpand?: boolean;
    expanded?: boolean;
    isLoading?: boolean;
    randomId?: string;
    loaded?: boolean;
    documents?: ISharepointDocumentLocation[];
    folders?: IFolderLocation[];
}

export interface ITessAccountLocation extends ISiteInfoLocation {
    accountId: string;
    projectFolders?: ITessProjectFolderLocation[];
}

export interface ITessAccountSite extends ISiteInfo {
    accountId: string;
    allowToggle?: boolean;
}

export interface ITessEmailRequest {
    accountId: string; // Salesforce ID of the Account
    workId?: string; // Salesforce ID of the Work (Project) - if applicable
    messageIdentifier?: string; // Identifier of the email message - internetMessageId
    threadIdentifier?: string; // Identifier of the email thread
    fromName?: string; // From: Name of the sender
    fromEmail: string; // From: Email address of the sender
    toEmails: string[]; // From: Email address of the receiver
    ccEmails: string[]; // CC: List of email addresses of other recipients
    incoming: boolean; // true = Retrieved email message, false = Sent email message
    subject: string;
    htmlBody: string;
    messageDate: string; // Send/Receive date-time
    attachments: ITessAttachmentLink[]; // List of links to attachments
}

export interface ITessAttachmentLink {
    label: string; // The label of filename of the attachment
    url: string; // The web-url of the attachment that is archived
}

export interface ITessEmailTaskResponse {
    ok: boolean; // Created successfully true/false
    errorMessage?: string; // Message on error
}

export const getStatusText = (languageStrings: any, status: TessProjectStatusEnum): string => {
    if (status === TessProjectStatusEnum.Active) return languageStrings.TessActiveProjects;
    if (status === TessProjectStatusEnum.Draft) return languageStrings.TessConceptProjects;
    if (status === TessProjectStatusEnum.Finished) return languageStrings.TessCompletedProjects;
    return null;
}

export const getTessAccounts = async (accountEmailAddresses?: string[]): Promise<ITessAccountsResult> => {
    // https://dalemeare.my.salesforce.com/services/apexrest/tess/AddressBook/email?email=info@acuity.nl&dossier=true
    // https://dalemeare.my.salesforce.com/services/apexrest/tess/AddressBook/all?dossier=true
    return await apiGetTessAccounts(accountEmailAddresses);
}

export const getTessAccountProjects = async (accountId: string): Promise<ITessProjectFolders> => {
    // https://dalemeare.my.salesforce.com/services/apexrest/tess/Work?accountId=0015J000006FUv0QAG&dossier=true
    const projectResult: ITessProjectFolders = await apiGetTessProjects(accountId);
    return projectResult;
}

export const saveEmail = async (tessAccountId: string, tessProjectId: string, webUrl: string, mode: ModeEnum, mailItem: any, succeededFiles: any[]) => {
    try {
        if (mailItem && succeededFiles?.length > 0) {
            let origin: string = new URL(webUrl).origin;
            if (origin.endsWith("/")) {
                origin = origin.slice(0, -1);
            }

            const savedAttachments: ITessAttachmentLink[] = succeededFiles
                .filter(f => f.isAttachment)
                .map(f => ({ label: f.name, url: origin + f.serverRelativeUrl }));

            let requestPayload: ITessEmailRequest;
            if (mode !== ModeEnum.mailCompose) {
                requestPayload = {
                    accountId: tessAccountId,
                    workId: tessProjectId,
                    messageIdentifier: mailItem.internetMessageId,
                    threadIdentifier: mailItem.conversationId,
                    fromName: mailItem.from?.displayName ?? mailItem.from?.emailAddress,
                    fromEmail: mailItem.from?.emailAddress ?? "",
                    toEmails: mailItem.to?.map(t => t.emailAddress) ?? [],
                    ccEmails: mailItem.cc?.map(cc => cc.emailAddress) ?? [],
                    incoming: mailItem.from?.emailAddress !== Office.context?.mailbox?.userProfile?.emailAddress,
                    subject: mailItem.subject,
                    htmlBody: await OutlookUtils.getMailItemHTMLBody(mailItem),
                    messageDate: mailItem?.dateTimeCreated?.toISOString(),
                    attachments: savedAttachments
                };
            }
            else {
                let mailboxAddress: string | undefined;
                if (Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                    mailboxAddress = await OutlookUtils.getMailboxAddress()
                }

                requestPayload = {
                    accountId: tessAccountId,
                    workId: tessProjectId,
                    messageIdentifier: mailItem.internetMessageId,
                    threadIdentifier: mailItem.conversationId,
                    fromName: mailboxAddress ?? Office.context?.mailbox?.userProfile?.displayName ?? Office.context?.mailbox?.userProfile?.emailAddress,
                    fromEmail: mailboxAddress ?? Office.context?.mailbox?.userProfile?.emailAddress ?? "",
                    toEmails: mailItem.toRecipients?.map(t => t.emailAddress?.address) ?? [],
                    ccEmails: mailItem.ccRecipients?.map(cc => cc.emailAddress?.address) ?? [],
                    incoming: false,
                    subject: mailItem.subject,
                    htmlBody: mailItem.body?.content ?? "",
                    messageDate: mailItem.sentDateTime ?? new Date().toISOString(),
                    attachments: savedAttachments
                };
            }

            await apiPostTessEmail(requestPayload);
        }
    } catch (error) {
        logger(error, "OutlookUtils.ts (saveEmail)");
    }
}

